<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useCartStore, type ShopCartGroup, type CartItem } from '@/stores/cart'
import { storefrontService } from '@/services/storefrontService'
import type { Product } from '@/types/shop'
import { formatMoney } from '@/utils/formatMoney'
import { useToast } from 'primevue/usetoast'
import { useStorefrontShop } from '@/composables/useStorefrontShop'
import { useShopBrand } from '@/composables/useShopBrand'

defineProps<{
  lightHeader?: boolean
}>()

const router = useRouter()
const cartStore = useCartStore()
const toast = useToast()

// Get shop context for custom domain support
const { getStorefrontPath, getProductRoute } = useStorefrontShop()
// On WL hosts the buyer is locked to one shop — there is no "marketplace"
// to browse. The empty-cart CTA points at the shop's own products list
// instead of /shops (which doesn't exist on the WL domain).
const { isWhiteLabel } = useShopBrand()

const validatingBasket = ref(false)
const updatingQuantity = ref<string | null>(null)
const sharingCart = ref<string | null>(null)
const showShareModal = ref(false)
const shareableLink = ref('')
const expiryCountdowns = ref<Map<string, { minutes: string; seconds: string }>>(new Map())
const suggestedProducts = ref<Product[]>([])
const loadingSuggestions = ref(false)

// Shop details for address display and min/max order validation
interface ShopDetails {
  address?: string
  city?: string
  postcode?: string
  minimumOrderAmount?: number
  maximumOrderAmount?: number
}
const shopDetailsMap = ref<Map<string, ShopDetails>>(new Map())

let countdownInterval: number | null = null

// Format price — per-shop currency (BE-authoritative). Dropdown can list
// carts from multiple shops in the marketplace, so callers pass the group's
// currency explicitly.
// Get image URL
function getImageUrl(url?: string): string | undefined {
  return storefrontService.getImageUrl(url) || undefined
}

// Update expiry countdowns with digital format
function updateExpiryCountdowns() {
  const newCountdowns = new Map<string, { minutes: string; seconds: string }>()

  cartStore.itemsByShop.forEach((group: ShopCartGroup) => {
    if (group.expiresAt) {
      const expiresAt = new Date(group.expiresAt)
      const now = new Date()
      const diff = expiresAt.getTime() - now.getTime()

      if (diff <= 0) {
        newCountdowns.set(group.shopId, { minutes: '00', seconds: '00' })
      } else {
        const minutes = Math.floor(diff / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)
        newCountdowns.set(group.shopId, {
          minutes: minutes.toString().padStart(2, '0'),
          seconds: seconds.toString().padStart(2, '0')
        })
      }
    }
  })

  expiryCountdowns.value = newCountdowns
}

// Start countdown timer
function startExpiryCountdown() {
  updateExpiryCountdowns()
  countdownInterval = window.setInterval(updateExpiryCountdowns, 1000)
}

// Load shop details (for address display and minimum order validation)
async function loadShopDetails() {
  for (const group of cartStore.itemsByShop) {
    if (!shopDetailsMap.value.has(group.shopId)) {
      try {
        const shop = await storefrontService.getShop(group.shopCode)
        if (shop) {
          shopDetailsMap.value.set(group.shopId, {
            address: shop.addressLine1,
            city: shop.city,
            postcode: shop.postcode,
            minimumOrderAmount: shop.minimumOrderAmount,
            maximumOrderAmount: shop.maximumOrderAmount
          })
        }
      } catch (error) {
        console.error('Error loading shop details:', error)
      }
    }
  }
}

// Check if cart meets minimum order requirement for a shop
function meetsMinimumOrder(shopId: string, subtotal: number): boolean {
  const shopDetails = shopDetailsMap.value.get(shopId)
  if (!shopDetails?.minimumOrderAmount) return true // No minimum set
  return subtotal >= shopDetails.minimumOrderAmount
}

// Get amount remaining to meet minimum order
function getAmountToMinimum(shopId: string, subtotal: number): number {
  const shopDetails = shopDetailsMap.value.get(shopId)
  if (!shopDetails?.minimumOrderAmount) return 0
  const remaining = shopDetails.minimumOrderAmount - subtotal
  return remaining > 0 ? remaining : 0
}

function meetsMaximumOrder(shopId: string, subtotal: number): boolean {
  const shopDetails = shopDetailsMap.value.get(shopId)
  if (!shopDetails?.maximumOrderAmount) return true
  return subtotal <= shopDetails.maximumOrderAmount
}

function getMaximumOrderAmount(shopId: string): number | undefined {
  return shopDetailsMap.value.get(shopId)?.maximumOrderAmount
}

function getAmountOverMaximum(shopId: string, subtotal: number): number {
  const max = shopDetailsMap.value.get(shopId)?.maximumOrderAmount
  if (!max) return 0
  const over = subtotal - max
  return over > 0 ? over : 0
}

// Get formatted address for shop
function getShopAddress(shopId: string): string | null {
  const details = shopDetailsMap.value.get(shopId)
  if (!details) return null
  const parts = [details.address, details.city, details.postcode].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

// Load suggested products from shop
async function loadSuggestedProducts() {
  const firstGroup = cartStore.itemsByShop[0]
  if (!firstGroup) return

  loadingSuggestions.value = true
  try {
    const cartProductIds = new Set(firstGroup.items.map((item: CartItem) => item.productId))

    const response = await storefrontService.getProducts(firstGroup.shopCode, {
      page: 1,
      pageSize: 6,
      sortBy: 'sales',
      sortDirection: 'desc'
    })

    suggestedProducts.value = response.items
      .filter((p: Product) => !cartProductIds.has(p.id))
      .slice(0, 4)
  } catch (error) {
    console.error('Error loading suggestions:', error)
  } finally {
    loadingSuggestions.value = false
  }
}

// Handle cart open/close
function openCart() {
  cartStore.openDropdown()
  if (!cartStore.isEmpty) {
    loadSuggestedProducts()
    loadShopDetails()
  }
}

function closeCart() {
  cartStore.closeDropdown()
  showShareModal.value = false
}

// Toggle cart
function toggleCart() {
  if (cartStore.isOpen) {
    closeCart()
  } else {
    openCart()
  }
}

// Update quantity with loading state
async function handleUpdateQuantity(itemId: string, newQuantity: number) {
  if (newQuantity < 1) {
    await cartStore.removeItem(itemId)
    return
  }

  updatingQuantity.value = itemId
  try {
    await cartStore.updateQuantity(itemId, newQuantity)
  } finally {
    updatingQuantity.value = null
  }
}

// Increment quantity
async function incrementQuantity(itemId: string, currentQty: number) {
  await handleUpdateQuantity(itemId, currentQty + 1)
}

// Decrement quantity
async function decrementQuantity(itemId: string, currentQty: number) {
  if (currentQty <= 1) {
    await cartStore.removeItem(itemId)
  } else {
    await handleUpdateQuantity(itemId, currentQty - 1)
  }
}

// Remove item with confirmation
async function removeItem(itemId: string, itemName: string) {
  await cartStore.removeItem(itemId)
  toast.add({
    severity: 'info',
    summary: 'Item Removed',
    detail: `${itemName} removed from cart`,
    life: 3000
  })
}

// Share cart
async function shareCart(shopId: string) {
  if (sharingCart.value) return

  sharingCart.value = shopId
  try {
    const link = await cartStore.getShareableLink(shopId)
    if (link) {
      const cartToken = cartStore.getCartToken(shopId)
      if (cartToken) {
        shareableLink.value = `${window.location.origin}/cart/${cartToken}`
        showShareModal.value = true
      }
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not generate shareable link',
        life: 3000
      })
    }
  } catch (error) {
    console.error('Error sharing cart:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to share cart',
      life: 3000
    })
  } finally {
    sharingCart.value = null
  }
}

// Copy link to clipboard
async function copyShareLink() {
  try {
    await navigator.clipboard.writeText(shareableLink.value)
    toast.add({
      severity: 'success',
      summary: 'Copied!',
      detail: 'Link copied to clipboard',
      life: 2000
    })
  } catch (error) {
    console.error('Failed to copy:', error)
    toast.add({
      severity: 'error',
      summary: 'Failed',
      detail: 'Could not copy link',
      life: 3000
    })
  }
}

// Add suggested product to cart
async function addSuggestedToCart(product: Product) {
  const firstGroup = cartStore.itemsByShop[0]
  if (!firstGroup) return

  const result = await cartStore.validateAndAddItem({
    shopId: firstGroup.shopId,
    shopCode: firstGroup.shopCode,
    shopName: firstGroup.shopName,
    productId: product.id,
    name: product.name,
    title: product.name,
    slug: product.slug,
    price: product.price,
    compareAtPrice: product.compareAtPrice,
    imageUrl: product.primaryImageUrl,
    quantity: 1
  })

  if (result.success) {
    toast.add({
      severity: 'success',
      summary: 'Added to Cart',
      detail: `${product.name} added`,
      life: 2000
    })
    await loadSuggestedProducts()
  } else {
    toast.add({
      severity: 'warn',
      summary: 'Could not add item',
      detail: result.message || 'Unable to add item to cart',
      life: 4000
    })
  }
}

// Navigate to checkout with validation.
// Pass shopCode explicitly — the composable's shopCode may be empty when the
// dropdown is opened from a page without a :shopCode URL param, which would
// otherwise silently redirect to "/".
async function goToCheckout(shopId: string, shopCode: string) {
  if (validatingBasket.value) return

  validatingBasket.value = true

  try {
    const validation = await cartStore.validateShopBasket(shopId)

    if (!validation.isValid) {
      const invalidCount = validation.invalidItems.length
      toast.add({
        severity: 'warn',
        summary: 'Some items unavailable',
        detail: `${invalidCount} item${invalidCount > 1 ? 's have' : ' has'} limited or no stock. Cart has been updated.`,
        life: 5000
      })

      await cartStore.applyValidationResult(shopId, validation.invalidItems)
      return
    }

    closeCart()
    const path = shopCode ? `/shop/${shopCode}/checkout` : getStorefrontPath('checkout')
    router.push(path)
  } catch (error) {
    console.error('Error validating basket:', error)
    closeCart()
    const path = shopCode ? `/shop/${shopCode}/checkout` : getStorefrontPath('checkout')
    router.push(path)
  } finally {
    validatingBasket.value = false
  }
}

// Navigate to product. Pass shopCode explicitly — the cart dropdown is
// mounted in a fixed header that may render on ANY route (including `/`
// or `/cart` where the composable's shopCode is null). Without the
// override vue-router throws "Missing required param 'shopCode'".
function goToProduct(shopCode: string | null | undefined, slug: string | undefined) {
  if (!slug) return
  closeCart()
  router.push(getProductRoute(slug, shopCode ?? null))
}

// Navigate to shop. Accept shopCode so the dropdown works from any host
// route — `getStorefrontPath()` alone reads the composable's shopCode
// which is null on `/` / `/cart` / any non-shop route, and returns `/`.
function goToShop(shopCode: string | null | undefined) {
  closeCart()
  if (shopCode) {
    router.push(`/shop/${shopCode}`)
  } else {
    router.push(getStorefrontPath())
  }
}

// Watch for cart changes to reload suggestions
watch(() => cartStore.itemsByShop, () => {
  if (!cartStore.isEmpty && cartStore.isOpen) {
    loadSuggestedProducts()
  }
}, { deep: true })

// Handle escape key
function handleKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && cartStore.isOpen) {
    closeCart()
  }
}

onMounted(() => {
  startExpiryCountdown()
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div class="relative">
    <!-- Cart Icon Button -->
    <button
      class="relative p-2 rounded-lg transition-colors"
      :class="lightHeader ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 hover:text-white hover:bg-gray-800'"
      @click="toggleCart"
    >
      <i class="pi pi-shopping-cart text-xl"></i>
      <!-- Badge -->
      <span
        v-if="cartStore.itemCount > 0"
        class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-white text-xs font-medium rounded-full bg-rose-500"
      >
        {{ cartStore.itemCount > 99 ? '99+' : cartStore.itemCount }}
      </span>
    </button>

    <!-- Slide-out Panel -->
    <Teleport to="body">
      <!-- Backdrop overlay -->
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="cartStore.isOpen"
          class="fixed inset-0 bg-black/50 z-[100]"
          @click="closeCart"
        ></div>
      </Transition>

      <!-- Cart Panel -->
      <Transition
        enter-active-class="transition-transform duration-300 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="cartStore.isOpen"
          class="fixed top-0 right-0 bottom-0 w-[65%] sm:w-[420px] md:w-[480px] bg-white shadow-2xl z-[101] flex flex-col"
        >
          <!-- Close Button (floating) -->
          <button
            class="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 bg-white hover:bg-gray-100 rounded-xl transition-colors shadow-sm"
            @click="closeCart"
          >
            <i class="pi pi-times text-xl"></i>
          </button>

          <!-- Empty Cart -->
          <div v-if="cartStore.isEmpty" class="flex-1 flex flex-col items-center justify-center px-6 py-12">
            <div class="w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-rose-50">
              <i class="pi pi-shopping-cart text-4xl text-rose-300"></i>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h3>
            <p class="text-gray-500 text-center mb-6 max-w-xs">
              {{ isWhiteLabel
                ? 'Browse the counter and add something to get started.'
                : 'Discover amazing products from local shops and add them to your cart' }}
            </p>
            <router-link
              :to="isWhiteLabel ? '/products' : '/shops'"
              class="px-6 py-3 font-medium text-white rounded-xl transition-colors bg-rose-500 hover:bg-rose-600"
              @click="closeCart"
            >
              {{ isWhiteLabel ? 'Browse products' : 'Browse Shops' }}
            </router-link>
          </div>

          <!-- Cart Content -->
          <div v-else class="flex-1 overflow-y-auto">
            <!-- Shop Groups -->
            <div
              v-for="group in cartStore.itemsByShop"
              :key="group.shopId"
              class="border-b border-gray-200"
            >
              <!-- Sticky Shop Name Header -->
              <div class="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
                <div class="flex items-center gap-3 px-4 py-3">
                  <!-- Shop Logo -->
                  <button
                    class="flex-shrink-0 hover:opacity-80 transition-opacity"
                    @click="goToShop(group.shopCode)"
                  >
                    <div
                      v-if="group.shopLogoUrl"
                      class="w-10 h-10 rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-200"
                    >
                      <img
                        :src="getImageUrl(group.shopLogoUrl)"
                        :alt="group.shopName"
                        class="w-full h-full object-cover"
                      />
                    </div>
                    <div
                      v-else
                      class="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-rose-500 to-rose-600"
                    >
                      <span class="text-white font-bold">
                        {{ group.shopName.charAt(0) }}
                      </span>
                    </div>
                  </button>

                  <!-- Shop Name & Address -->
                  <button
                    class="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                    @click="goToShop(group.shopCode)"
                  >
                    <h3 class="font-bold text-gray-900 truncate">{{ group.shopName }}</h3>
                    <p
                      v-if="getShopAddress(group.shopId)"
                      class="text-xs text-gray-500 truncate flex items-center gap-1"
                    >
                      <i class="pi pi-map-marker text-[10px]"></i>
                      {{ getShopAddress(group.shopId) }}
                    </p>
                    <p v-else class="text-xs text-gray-500">
                      {{ group.itemCount }} {{ group.itemCount === 1 ? 'item' : 'items' }}
                    </p>
                  </button>

                  <!-- Share Button -->
                  <button
                    class="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Share cart"
                    :disabled="sharingCart === group.shopId"
                    @click="shareCart(group.shopId)"
                  >
                    <i :class="sharingCart === group.shopId ? 'pi pi-spin pi-spinner' : 'pi pi-share-alt'" class="text-sm"></i>
                  </button>
                </div>
              </div>

              <!-- Items List - Compact -->
              <div class="divide-y divide-gray-100">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  class="px-4 py-3"
                >
                  <div class="flex gap-3">
                    <!-- Product Image - Smaller -->
                    <button
                      class="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100"
                      @click="goToProduct(group.shopCode, item.slug)"
                    >
                      <img
                        v-if="item.imageUrl"
                        :src="getImageUrl(item.imageUrl)"
                        :alt="item.name"
                        class="w-full h-full object-cover"
                      />
                      <div
                        v-else
                        class="w-full h-full flex items-center justify-center"
                      >
                        <i class="pi pi-box text-lg text-gray-400"></i>
                      </div>
                    </button>

                    <!-- Product Info - Compact -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-start justify-between gap-1">
                        <button
                          class="text-left flex-1 min-w-0"
                          @click="goToProduct(group.shopCode, item.slug)"
                        >
                          <h4 class="text-sm font-medium text-gray-900 line-clamp-1 hover:text-gray-600 transition-colors">
                            {{ item.name }}
                          </h4>
                          <!-- Variant/Size -->
                          <p v-if="item.variantName" class="text-xs text-gray-500 mt-0.5">
                            {{ item.variantName }}
                          </p>
                          <!-- Product Options -->
                          <div v-if="item.optionSelections?.length" class="mt-0.5">
                            <p v-for="(opt, oi) in item.optionSelections" :key="oi" class="text-xs text-gray-400">
                              {{ opt.groupName }}: {{ opt.choiceName }}
                              <span v-if="opt.priceAdjustment > 0" class="text-gray-300">(+{{ formatMoney(opt.priceAdjustment, group.currency) }})</span>
                            </p>
                          </div>
                          <!-- Price inline -->
                          <div class="flex items-center gap-1.5 mt-0.5">
                            <span class="text-sm font-semibold text-rose-600">
                              {{ formatMoney(item.price, group.currency) }}
                            </span>
                            <span
                              v-if="item.compareAtPrice && item.compareAtPrice > item.price"
                              class="text-xs text-gray-400 line-through"
                            >
                              {{ formatMoney(item.compareAtPrice, group.currency) }}
                            </span>
                            <!-- Stock Warning inline -->
                            <span
                              v-if="item.isInStock === false"
                              class="text-[10px] text-red-500 font-medium"
                            >
                              Out of stock
                            </span>
                          </div>
                        </button>
                        <button
                          class="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 rounded transition-all"
                          @click="removeItem(item.id, item.name)"
                        >
                          <i class="pi pi-times text-xs"></i>
                        </button>
                      </div>

                      <!-- Quantity Controls & Line Total - Compact -->
                      <div class="mt-2 flex items-center justify-between">
                        <div class="flex items-center bg-gray-100 rounded-md">
                          <button
                            class="w-7 h-7 rounded-md hover:bg-white flex items-center justify-center transition-all disabled:opacity-50"
                            :disabled="updatingQuantity === item.id"
                            @click="decrementQuantity(item.id, item.quantity)"
                          >
                            <i class="pi pi-minus text-[10px] text-gray-600"></i>
                          </button>
                          <span class="w-8 text-center font-medium text-gray-900 text-xs">
                            <i v-if="updatingQuantity === item.id" class="pi pi-spin pi-spinner text-[10px] text-rose-600"></i>
                            <span v-else>{{ item.quantity }}</span>
                          </span>
                          <button
                            class="w-7 h-7 rounded-md hover:bg-white flex items-center justify-center transition-all disabled:opacity-50"
                            :disabled="updatingQuantity === item.id"
                            @click="incrementQuantity(item.id, item.quantity)"
                          >
                            <i class="pi pi-plus text-[10px] text-gray-600"></i>
                          </button>
                        </div>

                        <!-- Line Total -->
                        <span class="text-sm font-bold text-gray-900">
                          {{ formatMoney(item.price * item.quantity, group.currency) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- Suggested Products Section -->
            <div v-if="suggestedProducts.length > 0" class="px-5 py-4 border-t border-gray-100">
              <div class="flex items-center gap-2 mb-4">
                <i class="pi pi-sparkles text-rose-500"></i>
                <h3 class="font-semibold text-gray-900">You Might Also Like</h3>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div
                  v-for="product in suggestedProducts"
                  :key="product.id"
                  class="group bg-gray-50 rounded-xl p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="goToProduct(cartStore.itemsByShop[0]?.shopCode, product.slug)"
                >
                  <div class="w-full aspect-square rounded-lg overflow-hidden bg-white mb-2">
                    <img
                      v-if="product.primaryImageUrl"
                      :src="getImageUrl(product.primaryImageUrl)"
                      :alt="product.name"
                      class="w-full h-full object-cover"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center">
                      <i class="pi pi-box text-gray-300 text-xl"></i>
                    </div>
                  </div>
                  <h4 class="text-sm font-medium text-gray-900 truncate group-hover:text-gray-600">
                    {{ product.name }}
                  </h4>
                  <div class="flex items-center justify-between mt-1">
                    <span class="font-semibold text-sm text-rose-600">
                      {{ formatMoney(product.price, cartStore.itemsByShop[0]?.currency) }}
                    </span>
                    <button
                      class="text-xs font-medium flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-rose-600 hover:bg-rose-100"
                      @click.stop="addSuggestedToCart(product)"
                    >
                      <i class="pi pi-plus text-[10px]"></i>
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky Checkout Footer -->
          <div
            v-if="cartStore.itemsByShop.length > 0"
            class="flex-shrink-0 border-t border-gray-200 bg-white px-5 py-4 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          >
            <!-- Multi-shop notice -->
            <div
              v-if="cartStore.shopCount > 1"
              class="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 px-3 py-2 rounded-lg mb-3"
            >
              <i class="pi pi-info-circle"></i>
              <span>Items from {{ cartStore.shopCount }} shops will create separate orders</span>
            </div>

            <!-- Minimum order encouragement (for single shop cart) -->
            <div
              v-if="cartStore.shopCount === 1 && !meetsMinimumOrder(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal)"
              class="flex items-center gap-2 text-sm text-gray-600 mb-3"
            >
              <i class="pi pi-shopping-bag text-rose-500"></i>
              <span>
                So close! Just <strong class="text-rose-600">{{ formatMoney(getAmountToMinimum(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal), cartStore.itemsByShop[0].currency) }}</strong> away from checkout
              </span>
            </div>

            <!-- Maximum order limit exceeded (for single shop cart) -->
            <div
              v-if="cartStore.shopCount === 1 && !meetsMaximumOrder(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal)"
              class="flex items-start gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3"
            >
              <i class="pi pi-exclamation-circle text-rose-600 mt-0.5"></i>
              <span>
                Order cap is <strong>{{ formatMoney(getMaximumOrderAmount(cartStore.itemsByShop[0].shopId)!, cartStore.itemsByShop[0].currency) }}</strong>. Please remove <strong>{{ formatMoney(getAmountOverMaximum(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal), cartStore.itemsByShop[0].currency) }}</strong> to continue.
              </span>
            </div>

            <div class="flex items-center justify-between mb-3">
              <div>
                <span class="text-gray-600 text-sm">Subtotal</span>
                <p class="text-xs text-gray-400">Delivery at checkout</p>
              </div>
              <span class="text-2xl font-bold text-gray-900">
                {{ formatMoney(cartStore.total, cartStore.itemsByShop[0]?.currency) }}
              </span>
            </div>
            <button
              class="w-full py-3.5 text-white font-semibold rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              :class="meetsMinimumOrder(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal) && meetsMaximumOrder(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal)
                ? 'bg-gray-900 hover:bg-black shadow-gray-900/25'
                : 'bg-gray-400 cursor-not-allowed'"
              :disabled="validatingBasket || !meetsMinimumOrder(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal) || !meetsMaximumOrder(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].subtotal)"
              @click="goToCheckout(cartStore.itemsByShop[0].shopId, cartStore.itemsByShop[0].shopCode)"
            >
              <i v-if="validatingBasket" class="pi pi-spin pi-spinner"></i>
              <span>{{ validatingBasket ? 'Validating...' : 'Checkout' }}</span>
              <i v-if="!validatingBasket" class="pi pi-arrow-right"></i>
            </button>
          </div>
        </div>
      </Transition>

      <!-- Share Modal -->
      <Transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showShareModal"
          class="fixed inset-0 z-[110] flex items-center justify-center p-4"
        >
          <div class="absolute inset-0 bg-black/60" @click="showShareModal = false"></div>
          <div class="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <button
              class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              @click="showShareModal = false"
            >
              <i class="pi pi-times"></i>
            </button>

            <div class="text-center mb-6">
              <div class="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center bg-rose-100">
                <i class="pi pi-share-alt text-2xl text-rose-600"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900">Share Your Cart</h3>
              <p class="text-gray-500 mt-2 text-sm">
                Anyone with this link can view and adopt your cart.
              </p>
            </div>

            <div class="flex gap-2 mb-4">
              <input
                :value="shareableLink"
                readonly
                class="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 truncate focus:outline-none"
              />
              <button
                class="px-4 py-3 text-white rounded-xl transition-colors bg-rose-500 hover:bg-rose-600"
                @click="copyShareLink"
              >
                <i class="pi pi-copy"></i>
              </button>
            </div>

            <div class="flex items-center justify-center gap-2 text-xs text-gray-400">
              <i class="pi pi-clock"></i>
              <span>Link expires in 1 hour</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
